import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Alert } from "reactstrap";
import request from "common/request";
import logger from "common/logger";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import { ADMIN_PAYMENT_METHOD } from "common/components/admin-payment-form-proptypes";
import EditDestinationPageModal from "./edit-destination-page-modal";
import EditBillingInfoModal from "./edit-billing-info-modal";
import RenewPaymentFlow from "./renew-payment-flow";
import CancelFlowModal from "./cancel-flow-modal";
import ReenableRenewalModal from "./reenable-renewal-modal";
import CampaignClientsModal from "../campaign-clients-modal";
import AddWeeksPurchaseFlow from "./add-weeks-purchase-flow";
import ActionsDropdown from "./actions-dropdown";
import AdminActions from "./admin-actions";
import CampaignGroupPropType from "../campaign-group-prop-type";
import AwaitingConfigurationAlert from "../awaiting-configuration-alert";
import scrollToSection from "./scroll-to-section";

const propTypes = {
  campaignGroup: CampaignGroupPropType.isRequired,
  campaignActions: PropTypes.shape({
    editAdCreativeLink: PropTypes.string,
    editDestinationPageLink: PropTypes.string,
    editFacebookPageLink: PropTypes.string,
    editTargetAudienceLink: PropTypes.string,
    editGeographiesLink: PropTypes.string,
    copyAdLink: PropTypes.string,
    shareOnFacebookLink: PropTypes.string,
    shareOnInstagramLink: PropTypes.string,
    editBillingInfoLink: PropTypes.string,
    cancelLink: PropTypes.string,
    showCancelModal: PropTypes.bool,
    reenableRenewalLink: PropTypes.string,
    networkConsoleLinks: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, url: PropTypes.string })
    )
  }).isRequired,
  destinationURL: PropTypes.string,
  onRefetch: PropTypes.func.isRequired,
  adminPaymentMethod: ADMIN_PAYMENT_METHOD
};

const CampaignActions = ({
  campaignGroup,
  campaignActions,
  onRefetch,
  destinationURL = null,
  adminPaymentMethod = null
}) => {
  const [isDestinationPageModalOpen, setIsDestinationPageModalOpen] = useState(false);
  const [isCampaignClientsModalOpen, setIsCampaignClientsModalOpen] = useState(false);
  const [isEditBillingInfoModalOpen, setIsEditBillingInfoModalOpen] = useState(false);
  const [isAddWeeksModalOpen, setIsAddWeeksModalOpen] = useState(false);
  const [isCancelFlowModalOpen, setIsCancelFlowModalOpen] = useState(
    !!(campaignActions.showCancelModal && campaignActions.cancelLink)
  );
  const [isReenableRenewalModalOpen, setIsReenableRenewalModalOpen] = useState(false);
  const [error, setError] = useState(null);

  const renewalOwed = campaignGroup.status === "payment_failed";
  const awaitingConfiguration = campaignGroup.status === "awaiting_configuration";

  const canSendSellerReport =
    ["RealEstateCampaign", "VideoRealEstateCampaign"].includes(campaignGroup.campaign_type) &&
    !["draft", "awaiting_configuration"].includes(campaignGroup.status);

  useEffect(() => {
    if (campaignActions.showCancelModal && campaignActions.cancelLink) {
      mixpanelService.track("Campaign.CancelLinkFollowed", {
        ...pageData.get("mixpanelData"),
        campaignId: campaignGroup.campaign_id,
        campaignType: campaignGroup.campaign_type
      });
    }
  }, []);

  const handleViewPerformance = e => {
    e.preventDefault();
    mixpanelService.track("Campaign.CampaignActionsClick", {
      ...pageData.get("mixpanelData"),
      action: "view_performance"
    });
    const performanceSection = document.getElementById("performance-section");
    scrollToSection(performanceSection);
  };

  const handleFinishAndLaunch = () => {
    mixpanelService.track("Campaign.CampaignActionsClick", {
      ...pageData.get("mixpanelData"),
      action: "finish_and_launch"
    });
  };

  const handleSendSellerReport = () => {
    mixpanelService.track("Campaign.CampaignActionsClick", {
      ...pageData.get("mixpanelData"),
      action: "send_seller_report"
    });
    setIsCampaignClientsModalOpen(true);
  };

  const handleCopyAd = async () => {
    setError(null);
    try {
      const response = await request.post(campaignActions.copyAdLink);
      window.location.href = response.data.redirect_url;
    } catch (e) {
      logger.notify(e);
      setError(e?.response?.data?.error || "An error occurred copying the ad. Please try again.");
    }
  };

  const editAdItems = [
    {
      label: "Edit Ad Creative",
      link: campaignActions.editAdCreativeLink,
      action: "edit_ad_creative"
    },
    {
      label: "Edit Destination Page",
      link: campaignActions.editDestinationPageLink,
      onClick: () => setIsDestinationPageModalOpen(true),
      action: "edit_destination_page"
    },
    {
      label: "Edit Facebook Page",
      link: campaignActions.editFacebookPageLink,
      action: "edit_facebook_page"
    },
    {
      label: "Edit Target Audience",
      link: campaignActions.editTargetAudienceLink,
      action: "edit_target_audience"
    },
    {
      label: "Edit Geographies",
      link: campaignActions.editGeographiesLink,
      action: "edit_geographies"
    }
  ].filter(item => item.link);

  const otherActionItems = [
    campaignGroup.add_weeks && {
      label: "Add Weeks",
      onClick: () => setIsAddWeeksModalOpen(true),
      action: "add_weeks"
    },
    campaignActions.shareOnFacebookLink && {
      label: "Share on Facebook",
      link: campaignActions.shareOnFacebookLink,
      target: "_blank",
      action: "share_on_facebook"
    },
    campaignActions.shareOnInstagramLink && {
      label: "Share on Instagram",
      link: campaignActions.shareOnInstagramLink,
      target: "_blank",
      action: "share_on_instagram"
    },
    campaignActions.copyAdLink && {
      label: "Copy Ad",
      onClick: handleCopyAd,
      action: "copy_ad"
    },
    campaignActions.editBillingInfoLink && {
      label: renewalOwed ? "Update Billing Information" : "Edit Billing Info",
      onClick: () => setIsEditBillingInfoModalOpen(true),
      action: renewalOwed ? "update_billing_information" : "edit_billing_info"
    },
    campaignActions.reenableRenewalLink && {
      label: "Re-enable Renewal",
      onClick: () => setIsReenableRenewalModalOpen(true),
      action: "reenable_renewal"
    },
    campaignActions.cancelLink && {
      label: "Cancel Campaign",
      onClick: () => setIsCancelFlowModalOpen(true),
      action: "cancel_campaign"
    }
  ].filter(Boolean);

  return (
    <div>
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      <h6 className="text-uppercase font-weight-semibold font-size-small mb-3">Campaign Actions</h6>
      {awaitingConfiguration ? (
        <>
          <Button
            color="success"
            size="md"
            className="btn-sharp w-100 mb-2 font-weight-semibold"
            tag="a"
            href={campaignGroup.finish_and_launch_link}
            onClick={handleFinishAndLaunch}
          >
            Finish and Launch
          </Button>
          <AwaitingConfigurationAlert
            daysRemaining={campaignGroup.awaiting_configuration_days_remaining}
          />
        </>
      ) : (
        <Button
          color="primary"
          size="md"
          className="btn-sharp w-100 mb-2 text-left d-flex overflow-hidden"
          tag="a"
          href="#performance-section"
          onClick={handleViewPerformance}
        >
          <span className="text-truncate">
            <i className="fa fa-chart-column mr-2" />
            View Performance
          </span>
        </Button>
      )}
      {canSendSellerReport && (
        <Button
          color="primary"
          size="md"
          className="btn-sharp w-100 mb-2 text-left d-flex overflow-hidden"
          onClick={handleSendSellerReport}
        >
          <span className="text-truncate">
            <i className="fa fa-envelope mr-2" />
            Send Seller Report
          </span>
        </Button>
      )}
      {editAdItems.length > 0 && (
        <ActionsDropdown label="Edit Ad" icon="pencil-square-o" items={editAdItems} />
      )}
      {otherActionItems.length > 0 && (
        <ActionsDropdown label="Other Actions" icon="ellipsis" items={otherActionItems} />
      )}
      {(campaignGroup.campaign_results_admin_links?.length > 0 ||
        campaignActions.networkConsoleLinks?.length > 0) && (
        <AdminActions
          actionLinks={campaignGroup.campaign_results_admin_links || []}
          networkConsoleLinks={campaignActions.networkConsoleLinks || []}
          onRefetch={onRefetch}
        />
      )}
      {campaignActions.editDestinationPageLink && (
        <EditDestinationPageModal
          isOpen={isDestinationPageModalOpen}
          onClose={() => setIsDestinationPageModalOpen(false)}
          currentUrl={destinationURL}
          saveUrl={campaignActions.editDestinationPageLink}
          campaignType={campaignGroup.campaign_type}
        />
      )}
      {campaignActions.editBillingInfoLink &&
        (renewalOwed ? (
          <RenewPaymentFlow
            campaignGroup={campaignGroup}
            isOpen={isEditBillingInfoModalOpen}
            onClose={() => setIsEditBillingInfoModalOpen(false)}
            onRefetch={onRefetch}
          />
        ) : (
          <EditBillingInfoModal
            isOpen={isEditBillingInfoModalOpen}
            onClose={() => setIsEditBillingInfoModalOpen(false)}
            editBillingInfoLink={campaignActions.editBillingInfoLink}
            campaignId={campaignGroup.campaign_id}
            campaignType={campaignGroup.campaign_type}
          />
        ))}
      {isCampaignClientsModalOpen && (
        <CampaignClientsModal
          campaignGroup={campaignGroup}
          toggleDisplay={() => setIsCampaignClientsModalOpen(!isCampaignClientsModalOpen)}
        />
      )}
      {isCancelFlowModalOpen && (
        <CancelFlowModal
          campaignId={campaignGroup.campaign_id}
          campaignType={campaignGroup.campaign_type}
          isOpen={isCancelFlowModalOpen}
          onClose={() => setIsCancelFlowModalOpen(false)}
          cancelLink={campaignActions.cancelLink}
        />
      )}
      {campaignActions.reenableRenewalLink && (
        <ReenableRenewalModal
          isOpen={isReenableRenewalModalOpen}
          onClose={() => setIsReenableRenewalModalOpen(false)}
          reenableRenewalLink={campaignActions.reenableRenewalLink}
        />
      )}
      {campaignGroup.add_weeks && (
        <AddWeeksPurchaseFlow
          campaignGroup={campaignGroup}
          isOpen={isAddWeeksModalOpen}
          onClose={() => setIsAddWeeksModalOpen(false)}
          adminPaymentMethod={adminPaymentMethod}
          onRefetch={onRefetch}
        />
      )}
    </div>
  );
};

CampaignActions.propTypes = propTypes;

export default CampaignActions;
